import React, { useState, useEffect } from 'react';
import { api } from '../../api.js';
import ManagedDropdown from './ManagedDropdown.jsx';

const blank = { name: '', type: '', serialNumber: '', status: 'Available', assignedTruckId: '', assignedDriverId: '', notes: '' };

function fromAccessory(a) {
  if (!a) return blank;
  return {
    name: a.name || '', type: a.type || '', serialNumber: a.serial_number || '', status: a.status || 'Available',
    assignedTruckId: a.assigned_truck_id || '', assignedDriverId: a.assigned_driver_id || '', notes: a.notes || ''
  };
}

const TABS = ['Basic Information', 'Assignment Log'];

export default function AccessoryFormModal({ accessory, onClose, onSaved }) {
  const [savedId, setSavedId] = useState(accessory?.id || null);
  const [form, setForm] = useState(() => fromAccessory(accessory));
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    api('/dispatch/trucks').then(setTrucks).catch(() => setTrucks([]));
    api('/dispatch/drivers').then(setDrivers).catch(() => setDrivers([]));
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // ── Assignment Log (tab 1) ───────────────────────────────────────────────
  const [log, setLog] = useState(null);
  const loadLog = () => { if (savedId) api(`/dispatch/accessories/${savedId}/log`).then(setLog); };
  useEffect(() => { if (tab === 1 && savedId) loadLog(); }, [tab, savedId]);

  const [logForm, setLogForm] = useState({ logDate: '', truckId: '', driverId: '', notes: '' });
  const addLog = async () => {
    if (!logForm.logDate && !logForm.truckId && !logForm.driverId && !logForm.notes.trim()) return;
    await api(`/dispatch/accessories/${savedId}/log`, { method: 'POST', body: logForm });
    setLogForm({ logDate: '', truckId: '', driverId: '', notes: '' });
    loadLog();
  };
  const deleteLog = async (id) => {
    await api(`/dispatch/accessories/${savedId}/log/${id}`, { method: 'DELETE' });
    loadLog();
  };

  const persist = async () => {
    if (!form.name.trim()) { setError('Accessory name is required.'); return null; }
    setSaving(true);
    setError(null);
    try {
      const body = { ...form, assignedTruckId: form.assignedTruckId || '', assignedDriverId: form.assignedDriverId || '' };
      const saved = savedId
        ? await api(`/dispatch/accessories/${savedId}`, { method: 'PATCH', body })
        : await api('/dispatch/accessories', { method: 'POST', body });
      setSavedId(saved.id);
      return saved;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndClose = async () => { if (await persist()) onSaved(); };
  const saveAndNext = async () => { if (await persist()) setTab(t => Math.min(t + 1, TABS.length - 1)); };
  const reset = () => setForm(accessory ? fromAccessory(accessory) : blank);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
        <div className="customer-modal-title">{accessory ? 'Edit accessory' : 'New accessory'}</div>

        <div className="customer-modal-tabs">
          <div className="form-tabs" style={{ marginBottom: 0 }}>
            {TABS.map((t, i) => (
              <button key={t} type="button" className={i === tab ? 'form-tab active' : 'form-tab'} onClick={() => setTab(i)}>{t}</button>
            ))}
          </div>
        </div>

        <div className="customer-modal-body">
          {tab === 0 && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Name of Accessory<br/><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Load Secure Bar" /></label>
                <label>Type of Accessory<br/>
                  <ManagedDropdown field="accessory_type" value={form.type} onChange={v => set('type', v)} />
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Serial Number<br/><input value={form.serialNumber} onChange={e => set('serialNumber', e.target.value)} /></label>
                <label>Status<br/>
                  <select value={form.status} onChange={e => set('status', e.target.value)}>
                    <option>Available</option><option>On Trip</option><option>Out of Service</option><option>Inactive</option>
                  </select>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Assigned Truck<br/>
                  <select value={form.assignedTruckId} onChange={e => set('assignedTruckId', e.target.value)}>
                    <option value="">— Not assigned —</option>
                    {trucks.map(t => <option key={t.id} value={t.id}>{t.unit_number}</option>)}
                  </select>
                </label>
                <label>Assigned Driver<br/>
                  <select value={form.assignedDriverId} onChange={e => set('assignedDriverId', e.target.value)}>
                    <option value="">— Not assigned —</option>
                    {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </label>
              </div>

              <label style={{ display: 'block' }}>Notes<br/><input value={form.notes} onChange={e => set('notes', e.target.value)} /></label>
            </>
          )}

          {tab === 1 && (
            !savedId ? <div className="empty">Save the accessory first to log where it's been.</div> : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr 1fr auto', gap: 10, marginBottom: 14 }}>
                  <input type="date" value={logForm.logDate} onChange={e => setLogForm(f => ({ ...f, logDate: e.target.value }))} />
                  <select value={logForm.truckId} onChange={e => setLogForm(f => ({ ...f, truckId: e.target.value }))}>
                    <option value="">Truck (optional)</option>
                    {trucks.map(t => <option key={t.id} value={t.id}>{t.unit_number}</option>)}
                  </select>
                  <select value={logForm.driverId} onChange={e => setLogForm(f => ({ ...f, driverId: e.target.value }))}>
                    <option value="">Driver (optional)</option>
                    {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  <input placeholder="Notes" value={logForm.notes} onChange={e => setLogForm(f => ({ ...f, notes: e.target.value }))} />
                  <button type="button" className="primary-btn" onClick={addLog}>+ Add</button>
                </div>
                {(log || []).length === 0 ? <div className="empty">No log entries yet.</div> : (
                  <table>
                    <thead><tr><th>Date</th><th>Truck</th><th>Driver</th><th>Notes</th><th></th></tr></thead>
                    <tbody>
                      {(log || []).map(l => (
                        <tr key={l.id}>
                          <td>{l.log_date || '—'}</td>
                          <td>{l.truck_unit_number || '—'}</td>
                          <td>{l.driver_name || '—'}</td>
                          <td>{l.notes || '—'}</td>
                          <td><button type="button" onClick={() => deleteLog(l.id)} style={{ color: 'var(--red)' }}>Delete</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )
          )}

          {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '16px 0 0' }}>{error}</p>}
        </div>

        <div className="customer-modal-footer">
          <div className="modal-actions" style={{ margin: 0 }}>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" onClick={reset}>Reset</button>
            <button type="button" className="primary-btn" onClick={saveAndNext} disabled={saving || tab === TABS.length - 1}>Save &amp; Next</button>
            <button type="button" className="primary-btn" onClick={saveAndClose} disabled={saving}>
              {saving ? 'Saving...' : accessory ? 'Save changes' : 'Create accessory'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
